// ── S3 (client half) — applying Atlas's `creates` ─────────────────────────────────────────────────────
// PURE: shapes and name-resolves a create request. The caller writes it and reconciles — this decides
// WHAT to write, never writes it, so it is testable without a page or a service.
//
// Atlas asking for a variable is still SELECTION: it is deciding what should exist to be chosen. The
// safety property is preserved because the NAME it suggests is not an identity — this resolves collisions
// and the caller reconciles, so the handle Atlas eventually picks is one IT DID NOT CHOOSE.

import { resolveName, seedValue } from '../../../../shared/components/atlas/variable-create.util';
// resolveName moved to the shared module (2026-09-16, data binding plan 1). Re-exported here because
// atlas-agent.service.ts and docs/benchmarks/forge-v2/harness/create-entry.ts import it from this file.
export { resolveName };

/** What Atlas asked for. Mirrors AtlasCreate in olympus-functions. */
export interface AtlasCreateRequest {
  name: string;
  type: string;
  scope: 'local' | 'global';
  /** Entity/EntityList only: the entity HANDLE from the pick-list (`$E_14`) whose records it carries. */
  holdsEntity?: string;
  /**
   * WHAT THE VARIABLE IS FOR, in the builder's terms — it becomes the variable's DESCRIPTION on the page,
   * permanently, and is the only thing a human opening the variable list later has to go on.
   *
   * Distinct from `why`, deliberately. `why` is addressed to Forge and is about THIS function ("idle
   * local Number, used as the loop counter"); `purpose` is addressed to whoever maintains the project and
   * is about the variable ("the row currently being edited in the transactions list"). One is discarded
   * after the run, the other outlives it.
   */
  purpose?: string;
  why?: string;
}

/** `$E_14` → the real entity. Supplied by the caller from the same view Atlas selected from. */
export type EntityLookup = (handle: string) => { entityId: string; entityName: string } | null;

/** What the caller writes. Matches AtlasAddVariableEvent, the path the Atlas UI already uses. */
export interface ResolvedCreate {
  scope: 'local' | 'global';
  name: string;
  /** Blockly type label, e.g. 'Boolean' — for logging/telemetry; the VALUE is what actually types it. */
  blocklyType: string;
  /** The seed value. Its SHAPE is what the registry reads to decide the platform type. */
  value: any;
  /** Set when the suggested name collided and a suffix was added. Surfaced, never silent. */
  renamedFrom?: string;
  /** Written onto the created variable as its lasting description — see AtlasCreateRequest.purpose. */
  purpose?: string;
  why?: string;
  /**
   * TRUE for Entity / EntityList, and it decides what happens NEXT (2026-08-10).
   *
   * A new SCALAR adds one line to the pick list and changes nothing else, so the run continues straight
   * to Forge with it hydrated alongside the picks. A new RECORD-CARRYING variable changes the SHAPE of
   * what is selectable — its entity's fields, its routes, its columns all become reachable — so a
   * selection made before it existed was made against a different world. There the run re-asks Atlas from
   * scratch instead of bolting the new handle onto a stale answer.
   */
  isRecord: boolean;
}

/** Rejected requests, with the reason. Never dropped — a create that vanishes looks like one never asked. */
export interface RejectedCreate { name: string; type: string; reason: string; }

/**
 * The four scalar seeds, verbatim from `newVariableTypeChanged` in atlas.component.ts.
 *
 * THE VALUE IS THE TYPE. The registry reads `typeof value` to classify a variable (string → String,
 * number → Number, boolean → Boolean, ISO string → TimeStamp), so a wrong seed silently produces a
 * wrong-typed variable rather than an error. Deriving these rather than re-inventing them is the point.
 */
const SEED: Record<string, () => any> = {
  // Was (2026-09-16, data binding plan 1): Boolean: () => false, String: () => '', Number: () => 0,
  // TimeStamp: () => new Date().toISOString(), GEOMETRY: () => ({ type: 'Polygon', coordinates: [] }).
  // The seeds now come from variable-create.util.ts so the agent path and the panel cannot drift.
  Boolean: () => seedValue('BOOLEAN'),
  String: () => seedValue('STRING'),
  Number: () => seedValue('NUMBER'),
  TimeStamp: () => seedValue('TIMESTAMP'),
  GEOMETRY: () => seedValue('GEOMETRY'),
};

/** Accepted spellings → the canonical scalar. Atlas is told the platform names; models drift anyway. */
const CANON: Record<string, keyof typeof SEED> = {
  boolean: 'Boolean', bool: 'Boolean', BOOLEAN: 'Boolean',
  string: 'String', text: 'String', STRING: 'String',
  number: 'Number', int: 'Number', integer: 'Number', float: 'Number', NUMBER: 'Number',
  timestamp: 'TimeStamp', date: 'TimeStamp', datetime: 'TimeStamp', TIMESTAMP: 'TimeStamp',
  geometry: 'GEOMETRY', GEOMETRY: 'GEOMETRY', geo: 'GEOMETRY', GEO: 'GEOMETRY',
};
/** Entity | EntityList | ARRAY_OF_OBJECT → which record shape, or null for a scalar. */
const recordKind = (t: string): 'ENTITY' | 'LIST' | null => {
  const k = (t || '').replace(/[\s_]/g, '').toLowerCase();
  if (k === 'entity' || k === 'entityvariable') return 'ENTITY';
  if (k === 'entitylist' || k === 'arrayofobject' || k === 'list' || k === 'entitylistvariable') return 'LIST';
  return null;
};
const canonical = (t: string): keyof typeof SEED | null => {
  const raw = (t || '').trim();
  if (SEED[raw]) return raw as keyof typeof SEED;
  return CANON[raw] || CANON[raw.toLowerCase()] || null;
};

// Was (2026-09-16, data binding plan 1): moved to variable-create.util.ts, re-exported above.
// /**
//  * Resolve a name against the names already in use, appending a letter on collision.
//  *
//  * `isSaving` → `isSavingA` → `isSavingB`. Letters rather than digits because a trailing digit reads as
//  * an index (`total2` looks like the second total) where a letter reads as a disambiguation. Names are
//  * restricted to letters/underscore by the create dialog's own sanitiser, so a digit would be stripped
//  * downstream anyway.
//  */
// export function resolveName(name: string, taken: Set<string>): string {
//   const base = (name || 'variable').replace(/[^a-zA-Z_]/g, '') || 'variable';
//   if (!taken.has(base)) return base;
//   for (let i = 0; i < 26; i++) {
//     const cand = base + String.fromCharCode(65 + i);
//     if (!taken.has(cand)) return cand;
//   }
//   // 26 collisions on one base name is not a naming problem, it is a loop — fail loudly rather than
//   // inventing an unbounded scheme nobody will recognise.
//   throw new Error(`cannot find a free name for "${base}" — 26 variants are taken`);
// }

/**
 * A Forge CONTEXT_REQUEST carries `{type, scope, purpose, reason}` and NO NAME — Forge describes what it
 * needs, it does not name variables. `resolveCreates` needs one, so it is derived here.
 *
 * Deliberately dumb and predictable, not clever. "row index of the transaction currently being edited"
 * → `rowIndex`. Stopwords go (they carry no identity), the first three content words survive, and the
 * result is camelCased. A name is a suggestion in this system anyway — `resolveName` resolves collisions
 * and reconcile mints the handle — so the bar is "a builder reading the variable list recognises it",
 * not "the perfect name".
 *
 * The TYPE is the fallback rather than a generic word: `newNumber` says more than `newVariable` when the
 * purpose is unusable.
 */
const STOP = new Set([
  'the', 'a', 'an', 'of', 'for', 'to', 'in', 'on', 'at', 'by', 'with', 'from', 'into', 'and', 'or',
  'is', 'be', 'been', 'being', 'was', 'are', 'that', 'this', 'it', 'its', 'currently', 'used',
  'variable', 'value', 'values', 'store', 'stores', 'storing', 'hold', 'holds', 'holding', 'need',
  'needed', 'needs', 'position', 'when', 'while', 'each', 'every', 'over',
]);
export function nameFromPurpose(purpose: string, type: string): string {
  const words = (purpose || '')
    .replace(/[^a-zA-Z\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !STOP.has(w.toLowerCase()))
    .slice(0, 3);
  if (!words.length) return `new${(type || 'Variable').replace(/[^a-zA-Z]/g, '') || 'Variable'}`;
  return words
    .map((w, i) => (i === 0 ? w[0].toLowerCase() + w.slice(1) : w[0].toUpperCase() + w.slice(1)))
    .join('')
    .replace(/[^a-zA-Z_]/g, '');
}

/**
 * Shape Atlas's creates into writable variables.
 *
 * RECORD-CARRYING VARIABLES ARE SUPPORTED, via the entity HANDLE rather than a name. Their shape is
 * `{ entityId, entityName, type, data }` and an empty `entityId` produces a variable that looks real,
 * appears in every picker and resolves to nothing — so the entity must be named. Atlas names it the only
 * way it names anything: with a handle from the pick-list (`$E_14`), which the caller resolves here.
 * That keeps the safety property intact — Atlas never types an entity name — and it is what makes
 * "query tasks into a new list" possible at all.
 *
 * A handle that resolves to nothing is REJECTED WITH THE REASON, never created hollow.
 *
 * @param taken  every name already in use in that scope — local and global are different namespaces.
 * @param lookup resolves an entity handle. Omit only when no record-carrying create can occur.
 */
export function resolveCreates(
  creates: AtlasCreateRequest[],
  takenLocal: Set<string>,
  takenGlobal: Set<string>,
  lookup?: EntityLookup,
): { resolved: ResolvedCreate[]; rejected: RejectedCreate[] } {
  const resolved: ResolvedCreate[] = [];
  const rejected: RejectedCreate[] = [];
  // Copies, so two creates in one batch cannot collide with each other — resolving against the live set
  // only would let both take the same free name.
  const local = new Set(takenLocal), global = new Set(takenGlobal);

  for (const c of creates || []) {
    if (!c || !c.name) { rejected.push({ name: String(c?.name || ''), type: String(c?.type || ''), reason: 'no name' }); continue; }
    if (c.scope !== 'local' && c.scope !== 'global') { rejected.push({ name: c.name, type: c.type, reason: `scope "${c.scope}" is not local or global` }); continue; }
    const t = canonical(c.type);
    const rec = recordKind(c.type);
    if (!t && !rec) {
      rejected.push({ name: c.name, type: c.type, reason: `unknown type — expected Boolean, String, Number, TimeStamp, Entity or EntityList` });
      continue;
    }
    let recValue: any = null;
    if (rec) {
      if (!c.holdsEntity) { rejected.push({ name: c.name, type: c.type, reason: `${rec === 'ENTITY' ? 'Entity' : 'EntityList'} needs holdsEntity — the entity's handle from the list` }); continue; }
      const ent = lookup?.(c.holdsEntity);
      if (!ent?.entityId) { rejected.push({ name: c.name, type: c.type, reason: `holdsEntity "${c.holdsEntity}" is not an entity in this page's list` }); continue; }
      // Shapes verbatim from atlas.component's newVariableTypeChanged — the registry reads this object to
      // classify the variable, so an invented shape produces a wrong-typed variable rather than an error.
      // Was (2026-09-16, data binding plan 1):
      // recValue = rec === 'ENTITY'
      //   ? { entityId: ent.entityId, entityName: ent.entityName, type: 'ENTITY', data: {} }
      //   : { entityId: ent.entityId, entityName: ent.entityName, type: 'ARRAY_OF_OBJECT', data: [] };
      recValue = seedValue(rec === 'ENTITY' ? 'ENTITY' : 'ARRAY_OF_OBJECT', { entityId: ent.entityId, entityName: ent.entityName });
    }
    const taken = c.scope === 'local' ? local : global;
    let name: string;
    try { name = resolveName(c.name, taken); }
    catch (e: any) { rejected.push({ name: c.name, type: c.type, reason: e?.message || 'name resolution failed' }); continue; }
    taken.add(name);
    resolved.push({
      scope: c.scope, name,
      blocklyType: rec ? (rec === 'ENTITY' ? 'ENTITY_VARIABLE' : 'ENTITY_LIST_VARIABLE') : (t as string),
      value: rec ? recValue : SEED[t as keyof typeof SEED](),
      renamedFrom: name !== c.name ? c.name : undefined,
      purpose: c.purpose,
      why: c.why,
      isRecord: !!rec,
    });
  }
  return { resolved, rejected };
}
